import { TUpdateCacheGroupUserParam, TUpdateCacheUserParam, TUpdateUserEventCallback, TUserEvent } from '../typings';
import { IFullGroupUser, IFullRoleGroupUser, IGroupUser } from '../../../../typings/model/user';
import { COMMON_CHANNEL_ID } from '../../../../constant/channel';
import EventBus from '../../../../util/event-bus';
import { getFullGroupUserList } from '../util';
import { UserManager } from '../../user';
import { SPECIAL_ROLE_ID } from '../../../../typings/proto';
import { RoleManager, RoleManagerUpdateType } from '../../role';
import { getGroupChannelKey } from '../../util';
import { SortUtil } from '../../../../util/sort';
import { SimpleUserManager } from '../../simple-user';

/** 自定义的所有成员的roleId */
const CUSTOM_ALL_USER_ROLE_ID = 'custom_all_user';

/**
 * 基本群用户信息管理中心
 */
class BaseGroupUserManager {
  /**
   * groupId + channelId聚类的群用户Map
   * - 一级key: groupId + channelId，二级key: userId
   */
  protected userMapByGroupChannel = new Map<string, Record<string, IGroupUser>>();
  /**
   * groupId + channelId + roleId聚类的群用户排序列表Map
   * - key: groupId + channelId，value: 按roleId聚类TGroupUser按首字母排序的用户数组
   */
  protected userSortListMapByGroupChannelRole = new Map<string, Map<string, Array<IGroupUser>>>();
  /** 事件监听器 */
  protected eventListener: EventBus<TUserEvent> = new EventBus();

  /**
   * 根据groupId + channelId，获取根据用户名角色Id聚类排序的列表
   * @param groupId 群id
   * @param channelId 频道id(默认：'#', 非私密频道用户信息共用)
   * @returns 根据用户名角色Id聚类排序的用户信息列表
   */
  public async getUserSortListByGroupChannelRole(
    groupId: string,
    channelId = COMMON_CHANNEL_ID,
  ): Promise<IFullRoleGroupUser[][]> {
    const key = `${groupId}${channelId}`;
    const groupUserListMapByRole = this.userSortListMapByGroupChannelRole.get(key)
      || new Map<string, Array<IGroupUser>>();
    let roleListByGroupChannel = await RoleManager.getInstance().getRoleListByGroupChannel(groupId);
    const userRoleIdListCurrent = Array.from(groupUserListMapByRole.keys());
    const userRoleIdListConfig = roleListByGroupChannel.map(role => role.id);
    if (userRoleIdListCurrent.some(item => userRoleIdListConfig.indexOf(item) === -1)) {
      roleListByGroupChannel = await RoleManager.getInstance().getRoleListByGroupChannel(
        groupId,
        channelId,
        RoleManagerUpdateType.updateImmediate,
      );
    }
    const fullGroupUserListByRole: IFullRoleGroupUser[][] = [];
    roleListByGroupChannel.forEach((roleInfo) => {
      const userList = groupUserListMapByRole.get(roleInfo.id);
      if (userList) {
        const fullGroupUserList = getFullGroupUserList(userList);
        if (fullGroupUserList.length > 0) {
          fullGroupUserListByRole.push(fullGroupUserList.map(item => ({ ...item, role: roleInfo })));
        }
      }
    });
    return fullGroupUserListByRole;
  }

  /**
   * 根据groupId + channelId，获取根据用户名角色Id聚类排序的列表
   * - 用户数据中没有具体对应的角色信息
   * @param groupId 群id
   * @param channelId 频道id(默认：'#', 非私密频道用户信息共用)
   * @returns 根据用户名角色Id聚类排序的用户信息列表
   */
  public async getMentionMemberByGroupChannelRole(
    groupId: string,
    channelId = COMMON_CHANNEL_ID,
  ): Promise<IFullGroupUser[][]> {
    const key = `${groupId}${channelId}`;
    const groupUserListMapByRole = this.userSortListMapByGroupChannelRole.get(key)
      || new Map<string, Array<IGroupUser>>();
    const fullGroupUserListByRole: IFullGroupUser[][] = [];
    const roleIdList = [String(SPECIAL_ROLE_ID.ID_BOT_GROUP), CUSTOM_ALL_USER_ROLE_ID];
    for (const roleId of roleIdList) {
      const userList = groupUserListMapByRole.get(roleId) || [];
      const fullGroupUserList = getFullGroupUserList(userList);
      if (fullGroupUserList.length > 0) {
        fullGroupUserListByRole.push(fullGroupUserList);
      }
    }

    return fullGroupUserListByRole;
  }

  /**
   * 添加用户信息状态变化的监听器
   * @param callback 回调函数
   */
  public addUserUpdateEventListener(callback: TUpdateUserEventCallback): void {
    this.eventListener.on('USER_UPDATE_EVENT', callback);
  }

  /**
   * 移除用户信息状态变化的监听器
   * @param callback 回调函数
   */
  public removeUserUpdateEventListener(callback: TUpdateUserEventCallback): void {
    this.eventListener.on('USER_UPDATE_EVENT', callback);
  }

  /**
   * 通知外部，用户信息状态有变化
   * @param groupId 群id
   * @param channelId 频道id(默认：'#', 非私密频道用户信息共用)
   * @param isBot 是否为机器人(默认：否)
   */
  protected notifyUserUpdateEvent(
    groupId: string,
    channelId = COMMON_CHANNEL_ID,
    isBot = false,
  ) {
    this.eventListener.emit('USER_UPDATE_EVENT', {
      groupId,
      channelId,
      isBot,
    });
  }

  /**
   * 更新缓存的用户数据
   * @param param 相关参数
   * - groupId 群id
   * - channelId 频道id(默认：'#', 非私密频道用户信息共用)
   * - addBaseUserList 基础用户数据列表(增量数据)
   * - addGroupUserList 群用户数据列表(增量数据)
   * - addDBUserList DB用户数据列表(增量数据)
   * - needReset 是否需要重置(默认：否)
   * - needUpdateGroupCustomAllUser 是否需要更新群的自定义所有用户(默认：是)
   */
  protected updateCacheUser({
    groupId,
    addBaseUserList,
    addGroupUserList,
    addDBUserList = [],
    needReset = false,
    channelId = COMMON_CHANNEL_ID,
    needUpdateGroupCustomAllUser = true,
  }: TUpdateCacheUserParam): void {
    // 1.聚类更新缓存的用户数据
    UserManager.getInstance().setUserList(addBaseUserList);
    this.updateCacheGroupUser({
      addGroupUserList,
      groupId,
      channelId,
      needReset,
      needUpdateGroupCustomAllUser,
    });
    SimpleUserManager.getInstance().updateUserInfo(addDBUserList);
    // 2.数据更新完毕后，通知外层订阅者同步更新数据
    this.notifyUserUpdateEvent(groupId, channelId);
  }

  /**
   * 更新缓存的群用户数据(根据roleId，聚类更新群频道的用户数据)
   * @param param 相关参数
   * - addGroupUserList 群用户数据列表(增量数据，后台已经排好序)
   * - groupId 群id
   * - channelId 频道id(默认：'#', 非私密频道用户信息共用)
   * - needReset 是否需要重置(默认：否)
   * - needUpdateGroupCustomAllUser 是否需要更新群的自定义所有用户(默认：是)
   */
  private updateCacheGroupUser({
    addGroupUserList,
    groupId,
    channelId = COMMON_CHANNEL_ID,
    needReset = false,
    needUpdateGroupCustomAllUser = true,
  }: TUpdateCacheGroupUserParam) {
    const key = getGroupChannelKey(groupId, channelId);
    if (needReset) {
      this.userMapByGroupChannel.set(key, {});
      this.userSortListMapByGroupChannelRole.set(key, new Map<string, Array<IGroupUser>>());
    }

    const userMap = this.userMapByGroupChannel.get(key) || {};
    const normalRoleId = String(SPECIAL_ROLE_ID.ID_NORMAL);
    let userSortListByRole = this.userSortListMapByGroupChannelRole.get(key) || new Map<string, Array<IGroupUser>>();
    let hasUpdateCustomAllUser = false;
    for (const user of addGroupUserList) {
      const { uid, roleId } = user;
      userMap[uid] = user;
      const sortListArr = userSortListByRole.get(roleId) || [];
      const index = sortListArr.findIndex(item => String(item.uid) === String(uid));
      if (index === -1) {
        sortListArr.push(user);
      } else {
        sortListArr[index] = user;
      }
      userSortListByRole.set(roleId, sortListArr);
      const canUpdateCustomAllUser = needUpdateGroupCustomAllUser && user.roleIdList.includes(normalRoleId);
      if (canUpdateCustomAllUser) {
        userSortListByRole = this.updateCustomAllUserSortList(userSortListByRole, user);
        hasUpdateCustomAllUser = true;
      }
    }
    this.userMapByGroupChannel.set(key, userMap);
    if (hasUpdateCustomAllUser) {
      const sortList = SortUtil.sortStringListByLetter(userSortListByRole.get(CUSTOM_ALL_USER_ROLE_ID) || []);
      userSortListByRole.set(CUSTOM_ALL_USER_ROLE_ID, sortList);
    }
    this.userSortListMapByGroupChannelRole.set(key, userSortListByRole);
  }

  /**
   * 更新自定义所有成员的用户排序列表
   * @param userSortListByRole 根据角色聚类的用户排序列表Map
   * @param user 需要更新的用户信息
   */
  private updateCustomAllUserSortList(userSortListByRole: Map<string, IGroupUser[]>, user: IGroupUser) {
    const sortList = userSortListByRole.get(CUSTOM_ALL_USER_ROLE_ID) || [];
    const index = sortList.findIndex(item => String(item.uid) === String(user.uid));
    if (index === -1) {
      sortList.push(user);
    } else {
      sortList[index] = user;
    }
    userSortListByRole.set(CUSTOM_ALL_USER_ROLE_ID, sortList);
    return userSortListByRole;
  }
}

export { BaseGroupUserManager };
